import { Spin, TreeSelect } from 'antd';
import type { DataNode } from 'antd/es/tree';
import React, { useMemo, useState, useEffect } from 'react';
import { useGetDocumentDiagnosisValuesQuery } from '@client/features/Datasets/services/api';
import Styles from '@ui/features/PatientFilters/components/Styles.module.scss';
import type { DocumentFilterCondition } from '../../types';

interface IcdCodeTreeSelectProps {
    condition: DocumentFilterCondition;
    groupId: string;
    updateCondition: (groupId: string, conditionId: string, updates: Partial<DocumentFilterCondition>) => void;
    multiSelectInValues: Record<string, any[]>;
    multiSelectOutValues: Record<string, any[]>;
    setMultiSelectInValues: React.Dispatch<React.SetStateAction<Record<string, any[]>>>;
    setMultiSelectOutValues: React.Dispatch<React.SetStateAction<Record<string, any[]>>>;
}

/**
 * Tree select component for ICD diagnosis codes with IN/OUT support
 */
export const IcdCodeTreeSelect: React.FC<IcdCodeTreeSelectProps> = ({
    condition,
    groupId,
    updateCondition,
    multiSelectInValues,
    multiSelectOutValues,
    setMultiSelectInValues,
    setMultiSelectOutValues,
}) => {
    const [conditionSearch, setConditionSearch] = useState('');
    const conditionKey = `${groupId}-${condition.id}`;

    const { data: diagnosisData, isLoading: isDiagnosisLoading } = useGetDocumentDiagnosisValuesQuery({
        search: conditionSearch,
        limit: 100,
    });

    // Build tree structure for diagnosis
    const diagnosisTreeData = useMemo<DataNode[]>(() => {
        const values = diagnosisData?.values;
        if (!values) return [];

        const groups: Record<string, Array<{ label: string; value: string; icd_code: string; count?: number }>> = {};

        values.forEach((v: any) => {
            if (!v.icd_code) return;
            const icdPrefix = v.icd_code.split('.')[0];
            if (!groups[icdPrefix]) {
                groups[icdPrefix] = [];
            }
            const label = v.label ?? v.value ?? v.icd_code;
            groups[icdPrefix].push({
                label,
                value: v.icd_code,
                icd_code: v.icd_code,
                count: v.count,
            });
        });

        return Object.entries(groups).map(([prefix, items]) => {
            let groupTitle = prefix;
            if (items.length > 0) {
                const firstLabel = items[0].label;
                groupTitle = firstLabel.split('(')[0].trim();
            }
            const finalTitle = `${groupTitle} (${prefix})`;

            return {
                title: finalTitle,
                key: `group-${prefix}`,
                value: `group-${prefix}`,
                children: items.map(item => {
                    const label = `${item.label} (${item.icd_code})${item.count ? ` [${item.count} docs]` : ''}`;
                    return {
                        title: label,
                        key: item.value,
                        value: item.value,
                        isLeaf: true,
                        conceptId: item.value,
                        icdValue: item.value,
                    };
                }),
            };
        });
    }, [diagnosisData?.values]);

    // Initialize multiSelectInValues and multiSelectOutValues from condition when condition changes
    // This ensures selected values are restored when returning to the step
    // Wait for diagnosisTreeData to be loaded so we can map titles correctly
    useEffect(() => {
        if (!condition.value || !Array.isArray(condition.value) || condition.value.length === 0) {
            return;
        }

        // Don't initialize if tree data is not loaded yet (we need it for title mapping)
        if (!diagnosisTreeData || diagnosisTreeData.length === 0) {
            return;
        }

        const valueStatuses = condition.valueStatuses || {};
        const inValues: string[] = [];
        const outValues: string[] = [];

        condition.value.forEach((val: any) => {
            const stringVal = String(val);
            if (valueStatuses[stringVal] === 'OUT') {
                outValues.push(stringVal);
            } else {
                inValues.push(stringVal);
            }
        });

        // Only update if state is empty or different (to avoid unnecessary updates)
        const currentInValues = multiSelectInValues[conditionKey] || [];
        const currentOutValues = multiSelectOutValues[conditionKey] || [];
        const currentInValueArray = currentInValues.map(v =>
            typeof v === 'object' && v.value !== undefined ? v.value : v
        );
        const currentOutValueArray = currentOutValues.map(v =>
            typeof v === 'object' && v.value !== undefined ? v.value : v
        );

        // Helper to find title from tree data
        const findTitle = (val: string): string => {
            const findInNodes = (nodes: any[]): string | null => {
                for (const node of nodes) {
                    if (node.value === val || node.icdValue === val || node.conceptId === val) {
                        return node.title || String(val);
                    }
                    if (node.children) {
                        const found = findInNodes(node.children);
                        if (found) return found;
                    }
                }
                return null;
            };
            return findInNodes(diagnosisTreeData) || String(val);
        };

        // Check if we need to update IN values
        const inValuesChanged =
            inValues.length !== currentInValueArray.length || !inValues.every(val => currentInValueArray.includes(val));
        if (inValuesChanged && inValues.length > 0) {
            const inValuesWithTitles = inValues.map(val => ({
                value: val,
                title: findTitle(val),
            }));
            setMultiSelectInValues(prev => ({
                ...prev,
                [conditionKey]: inValuesWithTitles,
            }));
        }

        // Check if we need to update OUT values
        const outValuesChanged =
            outValues.length !== currentOutValueArray.length ||
            !outValues.every(val => currentOutValueArray.includes(val));
        if (outValuesChanged && outValues.length > 0) {
            const outValuesWithTitles = outValues.map(val => ({
                value: val,
                title: findTitle(val),
            }));
            setMultiSelectOutValues(prev => ({
                ...prev,
                [conditionKey]: outValuesWithTitles,
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [condition.value, condition.valueStatuses, conditionKey, diagnosisTreeData]);

    // Helper to update condition value from IN/OUT arrays
    const updateConditionFromInOut = (newInValues: any[], newOutValues: any[]) => {
        const inValueArray = newInValues.map(v => (typeof v === 'object' && v.value !== undefined ? v.value : v));
        const outValueArray = newOutValues.map(v => (typeof v === 'object' && v.value !== undefined ? v.value : v));
        const allValues = [...inValueArray, ...outValueArray];

        const valueStatuses: Record<string, 'IN' | 'OUT'> = {};
        inValueArray.forEach(val => {
            valueStatuses[String(val)] = 'IN';
        });
        outValueArray.forEach(val => {
            valueStatuses[String(val)] = 'OUT';
        });

        // Build valueTitleMap from newInValues and newOutValues
        const valueTitleMap: Record<string, string> = {};
        [...newInValues, ...newOutValues].forEach((item: any) => {
            if (typeof item === 'object' && item.value !== undefined && item.title !== undefined) {
                valueTitleMap[String(item.value)] = item.title;
            }
        });

        updateCondition(groupId, condition.id, {
            value: allValues.length > 0 ? allValues : undefined,
            valueStatuses: Object.keys(valueStatuses).length > 0 ? valueStatuses : undefined,
            valueTitleMap: Object.keys(valueTitleMap).length > 0 ? valueTitleMap : undefined,
        });
    };

    // Helper to handle value selection for IN or OUT
    const handleValueSelect = (selectedValues: any[], isIn: boolean) => {
        const prevValues = isIn ? multiSelectInValues[conditionKey] || [] : multiSelectOutValues[conditionKey] || [];

        // Create a map of existing values with their titles
        const existingValuesMap = new Map<string, { value: any; title: string }>();
        prevValues.forEach((item: any) => {
            const val = typeof item === 'object' && item.value !== undefined ? item.value : item;
            const title = typeof item === 'object' && item.title !== undefined ? item.title : String(val);
            existingValuesMap.set(String(val), { value: val, title });
        });

        // Build new values list with titles
        const newValuesWithTitles: Array<{ value: any; title: string }> = [];
        selectedValues.forEach((val: any) => {
            // Try to find title from tree data
            const findTitle = (nodes: any[]): string | null => {
                for (const node of nodes) {
                    if (node.value === val || node.icdValue === val || node.conceptId === val) {
                        return node.title || String(val);
                    }
                    if (node.children) {
                        const found = findTitle(node.children);
                        if (found) return found;
                    }
                }
                return null;
            };
            const title = findTitle(diagnosisTreeData) || existingValuesMap.get(String(val))?.title || String(val);
            newValuesWithTitles.push({ value: val, title });
        });

        // Update state
        if (isIn) {
            setMultiSelectInValues(prev => ({
                ...prev,
                [conditionKey]: newValuesWithTitles,
            }));
        } else {
            setMultiSelectOutValues(prev => ({
                ...prev,
                [conditionKey]: newValuesWithTitles,
            }));
        }

        // Update condition value with all values (IN + OUT)
        const otherValues = isIn ? multiSelectOutValues[conditionKey] || [] : multiSelectInValues[conditionKey] || [];
        updateConditionFromInOut(isIn ? newValuesWithTitles : otherValues, isIn ? otherValues : newValuesWithTitles);
    };

    // Create disabled tree data for In/Out
    const createDisabledTreeData = (nodes: any[], disabledValues: any[]): any[] => {
        if (!nodes || nodes.length === 0) return [];

        const disabledValueArray = disabledValues.map(v =>
            typeof v === 'object' && v.value !== undefined ? v.value : v
        );

        return nodes.map(node => {
            const processedChildren = node.children ? createDisabledTreeData(node.children, disabledValues) : undefined;
            const isLeafDisabled = node.isLeaf === true && node.value && disabledValueArray.includes(node.value);
            const allChildrenDisabled =
                node.isLeaf !== true &&
                processedChildren &&
                Array.isArray(processedChildren) &&
                processedChildren.length > 0
                    ? processedChildren.every((child: any) => child && child.disabled === true)
                    : false;
            const isDisabled = isLeafDisabled || allChildrenDisabled;

            return {
                ...node,
                disabled: isDisabled,
                children: processedChildren,
            };
        });
    };

    const inValues = multiSelectInValues[conditionKey] || [];
    const outValues = multiSelectOutValues[conditionKey] || [];
    const inValueArray = inValues.map(v => (typeof v === 'object' && v.value !== undefined ? v.value : v));
    const outValueArray = outValues.map(v => (typeof v === 'object' && v.value !== undefined ? v.value : v));

    const inTreeData = createDisabledTreeData(diagnosisTreeData, outValues);
    const outTreeData = createDisabledTreeData(diagnosisTreeData, inValues);

    return (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {/* In (IN) TreeSelect */}
            <div>
                <div style={{ marginBottom: 4, fontSize: 12, fontWeight: 500, color: '#666' }}>In</div>
                <TreeSelect
                    multiple
                    treeData={inTreeData}
                    value={inValueArray}
                    onChange={values => handleValueSelect(values || [], true)}
                    placeholder="Select diagnoses..."
                    allowClear
                    showSearch
                    treeCheckable
                    treeDefaultExpandAll={false}
                    maxTagCount="responsive"
                    style={{ width: '100%' }}
                    className={Styles.filterSelectbox}
                    loading={isDiagnosisLoading}
                    notFoundContent={isDiagnosisLoading ? <Spin size="small" /> : 'No diagnoses found'}
                    onSearch={setConditionSearch}
                    filterTreeNode={(inputValue, node) => {
                        const title = node.title as string;
                        return title?.toLowerCase().includes(inputValue.toLowerCase()) || false;
                    }}
                />
            </div>

            {/* Out (OUT) TreeSelect */}
            <div>
                <div style={{ marginBottom: 4, fontSize: 12, fontWeight: 500, color: '#666' }}>Out</div>
                <TreeSelect
                    multiple
                    treeData={outTreeData}
                    value={outValueArray}
                    onChange={values => handleValueSelect(values || [], false)}
                    placeholder="Select diagnoses..."
                    allowClear
                    showSearch
                    treeCheckable
                    treeDefaultExpandAll={false}
                    maxTagCount="responsive"
                    style={{ width: '100%' }}
                    className={Styles.filterSelectbox}
                    loading={isDiagnosisLoading}
                    notFoundContent={isDiagnosisLoading ? <Spin size="small" /> : 'No diagnoses found'}
                    onSearch={setConditionSearch}
                    filterTreeNode={(inputValue, node) => {
                        const title = node.title as string;
                        return title?.toLowerCase().includes(inputValue.toLowerCase()) || false;
                    }}
                />
            </div>
        </div>
    );
};
